
import Vue from "vue";
import _ from "lodash";
import { ListAction } from "@/features/Actions/Action";
import User from "@/shared/models/User";
import { UserType } from "@/features/Authentication/PlatformUserInfoService";
import LabeledItem from "@/shared/components/LabeledItem.vue";
import FileUpload, { FileUploadRef } from "@/features/Documents/FileUpload.vue";
import { generateUUID } from "@/shared/utils/common_utils";
import FileList, { FileListDocumentType } from "@/features/Documents/FileList.vue";
import AppError from "@/shared/components/AppError.vue";
import UploadedPlatformDocumentMetadata from "@/features/PlatformDocuments/UploadedPlatformDocumentMetadata";

const USER_OPTIONS_LIMIT = 10;
const DEBOUNCE_TIMER = 200; // Milliseconds

export default Vue.extend({
    name: "ActionDetailsModal",
    components: {
        AppError,
        FileUpload,
        FileList,
        LabeledItem,
    },
    data: () => ({
        dialog: false,
        dataAction: null! as ListAction,
        // vue-form rules are eagerly evaluated as soon as an input field is selected and deselected, and displays the error messages defined in the rules.
        // We want to match the existing QRev patterns and only show errors on submission, so we start with the rules null, and add the rules on submission to display
        // error messages to the user
        rules: {
            atLeastOneUserSelected: (value: string[]): any => {
                return value.length > 0 || "Please select at least 1 user";
            },
            descriptionRequired: (value: string): any => {
                return (value != null && value != "") || "Please provide a description";
            },
        },
        rulesEnabled: false,
        users: [] as User[], // Users to choose from
        isLoadingUsers: false,
        isCreating: false, // If the Action passed in has a null ID, we are creating a new Action
        query: null! as string,
        documentWorkspaceID: null as string | null,
        errorMessage: "",
        showError: false,
        requestInProgress: false,
        fileListDocumentTypes: FileListDocumentType,
        searchTimeoutID: 0,
    }),
    props: {
        parentID: {
            type: String,
            required: false,
            default: "",
        },
        action: {
            type: ListAction,
            required: true,
        },
        showModal: {
            type: Boolean,
            required: true,
        },
    },
    computed: {
        items(): User[] {
            const assignableUsers = this.users.map(user => {
                const description = `${user.name} (${user.email})`;

                // Add description field to object for displaying name and email
                return Object.assign({}, user, { description });
            });
            return assignableUsers;
        },
    },
    watch: {
        async query(val): Promise<void> {
            window.clearTimeout(this.searchTimeoutID); // Delay search a bit until the user stops typing
            this.searchTimeoutID = window.setTimeout(async () => {
                await this.getUsers(val);
            }, DEBOUNCE_TIMER);
        },
        // If the users on the dataAction change, clear the query
        "dataAction.users": function () {
            this.query = "";
        },
        action() {
            this.dataAction = _.cloneDeep(this.action);
        },
        showModal(val: boolean) {
            this.dialog = val;
            // If we open the window, set the dataAction
            if (this.dialog) {
                // Copy action
                this.dataAction = _.cloneDeep(this.action);
                // Prepopulate the users
                if (this.dataAction.users) {
                    this.users = [...this.dataAction.users, ...this.users];
                }
                // Set isCreating
                if (this.dataAction && this.dataAction.id) {
                    this.isCreating = false;
                } else {
                    this.isCreating = true;
                }
                this.documentWorkspaceID = generateUUID();
            }
        },
    },
    methods: {
        async getUsers(search: string) {
            // Items have already been requested
            if (this.isLoadingUsers) {
                return;
            }
            // No query
            if (search === null || search === "") {
                return;
            }

            // Lazily load users
            this.isLoadingUsers = true;
            try {
                const users = await this.$actionService.getAssignableUsers(search, USER_OPTIONS_LIMIT, [UserType.Internal]);

                // Include any existing selections in the list of options so they show as selected
                this.users = [...this.dataAction.users, ...users];
            } catch (e) {
                this.errorMessage = "Failed to get users";
                this.showError = true;
            } finally {
                this.isLoadingUsers = false;
            }
        },
        async saveAction(): Promise<void> {
            // Add the rules for verification
            this.rulesEnabled = true;
            this.$nextTick(async () => {
                // Manually trigger Vuetify validation
                if ((this.$refs.actionForm as any).validate()) {
                    try {
                        this.requestInProgress = true;
                        if (this.dataAction) {
                            // Get the IDs off the users
                            this.dataAction.userIDs = [];
                            if (this.dataAction.users.length > 0) {
                                this.dataAction.users.forEach(user => {
                                    this.dataAction.userIDs.push(user.id);
                                });
                            }
                            if (this.isCreating) {
                                console.log("Sending POST action...");
                                await this.$actionService.createAction(this.dataAction);
                                this.onActionSaved();
                            } else {
                                console.log("Sending PUT action...");
                                await this.$actionService.updateAction(this.dataAction);
                                this.onActionSaved();
                            }
                            this.dialog = false;
                            this.$emit("modalClosed");
                        }
                    } catch (e) {
                        this.errorMessage = "Failed to save action";
                        this.showError = true;
                    } finally {
                        this.requestInProgress = false;
                    }
                }
                //if not valid, displays errors defined in the rules
            });
        },
        closeModal() {
            // Close the modal and reset values
            this.dialog = false;
            this.query = "";
            this.dataAction.users = [];
            this.rulesEnabled = false;
            this.showError = false;
            this.errorMessage = "";
            this.dataAction.uploadedPlatformDocumentMetadatas = [];
            (this.$refs.fileUpload as FileUploadRef).reset();
            (this.$refs.actionForm as any).reset();
            this.$emit("modalClosed");
        },
        onActionSaved() {
            // Close the modal and reset values
            this.dialog = false;
            this.query = "";
            this.dataAction.users = [];
            this.rulesEnabled = false;
            this.showError = false;
            this.errorMessage = "";
            this.dataAction.uploadedPlatformDocumentMetadatas = [];
            (this.$refs.fileUpload as FileUploadRef).reset();
            (this.$refs.actionForm as any).reset();
            // This causes the action list to reload
            this.$emit("actionSaved");
        },
        documentUploaded(uploadedPlatformDocumentMetadata: UploadedPlatformDocumentMetadata): void {
            if (!this.dataAction.uploadedPlatformDocumentMetadatas) {
                this.dataAction.uploadedPlatformDocumentMetadatas = [];
            }
            this.dataAction.uploadedPlatformDocumentMetadatas.push(uploadedPlatformDocumentMetadata);
        },
        documentRemoved(removedDocumentMetadataID: string): void {
            this.dataAction.uploadedPlatformDocumentMetadatas = this.dataAction.uploadedPlatformDocumentMetadatas.filter(updm => updm.uploadedPlatformDocument.documentID != removedDocumentMetadataID);
        },
        uploadChanged(uploadInProgress: boolean) {
            this.requestInProgress = uploadInProgress;
        },
    },
});
